import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './Layout';
import { authFetch, getUser } from '../../utils/auth';
import {
  FileText,
  Eye,
  CheckCircle,
  Clock,
  TrendingUp,
  Calendar,
  PlusCircle,
  Users,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = getUser();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await authFetch('/api/blogs/stats/overview');
      if (response) {
        const data = await response.json();
        if (data.success) setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const statCards = [
    {
      title: 'Total Blogs',
      value: stats?.totalBlogs ?? '—',
      icon: FileText,
      gradient: 'linear-gradient(135deg, #25343F 0%, #3a4f5e 100%)',
      accent: '#BFC9D1',
    },
    {
      title: 'Published',
      value: stats?.publishedBlogs ?? '—',
      icon: CheckCircle,
      gradient: 'linear-gradient(135deg, #FF9B51 0%, #e07a33 100%)',
      accent: 'rgba(255,255,255,0.8)',
    },
    {
      title: 'Drafts',
      value: stats?.draftBlogs ?? '—',
      icon: Clock,
      gradient: 'linear-gradient(135deg, #BFC9D1 0%, #97a8b4 100%)',
      accent: '#25343F',
    },
    {
      title: 'Total Views',
      value: stats?.totalViews ?? '—',
      icon: Eye,
      gradient: 'linear-gradient(135deg, #25343F 0%, #FF9B51 100%)',
      accent: '#EAEFEF',
    },
  ];

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div
            className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: '#FF9B51', borderTopColor: 'transparent' }}
          />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* Welcome Header */}
        <div
          className="rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ background: 'linear-gradient(135deg, #25343F 0%, #3a4f5e 100%)' }}
        >
          <div>
            <p className="text-sm mb-1" style={{ color: '#BFC9D1' }}>{today}</p>
            <h1 className="text-2xl font-bold text-white">
              Welcome back, <span style={{ color: '#FF9B51' }}>{user?.name || user?.username}</span>!
            </h1>
            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize" style={{ backgroundColor: 'rgba(255,155,81,0.2)', color: '#FF9B51' }}>
              {user?.role}
            </div>
          </div>
          <Link
            to="/admin/blogs/create"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90 active:scale-95 shadow-accent flex-shrink-0"
            style={{ backgroundColor: '#FF9B51' }}
          >
            <PlusCircle className="w-4 h-4" />
            New Blog Post
          </Link>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={i}
                className="rounded-2xl p-5 text-white shadow-brand"
                style={{ background: card.gradient }}
              >
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium" style={{ color: card.accent }}>{card.title}</p>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">
                  {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Two Column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Popular Blogs */}
          <div className="bg-white rounded-2xl shadow-brand border p-6" style={{ borderColor: '#BFC9D1' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold flex items-center gap-2" style={{ color: '#25343F' }}>
                <TrendingUp className="w-4 h-4" style={{ color: '#FF9B51' }} />
                Popular Blogs
              </h2>
              <Link to="/admin/blogs" className="flex items-center gap-1 text-xs font-medium hover:opacity-70 transition-opacity" style={{ color: '#FF9B51' }}>
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="space-y-2">
              {stats?.popularBlogs?.length > 0 ? (
                stats.popularBlogs.map((blog, idx) => (
                  <div key={blog._id} className="flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg,#FF9B51,#e07a33)' }}
                      >
                        {idx + 1}
                      </div>
                      <Link to={`/admin/blogs/edit/${blog.slug}`} className="text-sm font-medium truncate hover:opacity-70" style={{ color: '#25343F' }}>
                        {blog.title}
                      </Link>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0" style={{ color: '#BFC9D1' }}>
                      <Eye className="w-3.5 h-3.5" />
                      <span className="text-xs font-semibold">{blog.viewCount}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center py-8 text-sm" style={{ color: '#BFC9D1' }}>No blogs yet</p>
              )}
            </div>
          </div>

          {/* Recent Blogs */}
          <div className="bg-white rounded-2xl shadow-brand border p-6" style={{ borderColor: '#BFC9D1' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold flex items-center gap-2" style={{ color: '#25343F' }}>
                <Calendar className="w-4 h-4" style={{ color: '#FF9B51' }} />
                Recent Blogs
              </h2>
              <Link to="/admin/blogs" className="flex items-center gap-1 text-xs font-medium hover:opacity-70 transition-opacity" style={{ color: '#FF9B51' }}>
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="space-y-2">
              {stats?.recentBlogs?.length > 0 ? (
                stats.recentBlogs.map((blog) => (
                  <div key={blog._id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="min-w-0">
                      <Link to={`/admin/blogs/edit/${blog.slug}`} className="text-sm font-medium block truncate hover:opacity-70" style={{ color: '#25343F' }}>
                        {blog.title}
                      </Link>
                      <p className="text-xs mt-0.5" style={{ color: '#BFC9D1' }}>{formatDate(blog.createdAt)}</p>
                    </div>
                    <span
                      className="ml-3 px-2.5 py-1 text-xs font-semibold rounded-full flex-shrink-0"
                      style={blog.isActive
                        ? { backgroundColor: 'rgba(255,155,81,0.12)', color: '#FF9B51' }
                        : { backgroundColor: '#EAEFEF', color: '#BFC9D1' }
                      }
                    >
                      {blog.isActive ? 'Published' : 'Draft'}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-center py-8 text-sm" style={{ color: '#BFC9D1' }}>No blogs yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: '/admin/blogs/create', icon: PlusCircle, label: 'Create Blog', sub: 'Write a new post' },
            { to: '/admin/blogs', icon: FileText, label: 'Manage Blogs', sub: 'Edit or delete posts' },
            { to: '/admin/users', icon: Users, label: 'User Management', sub: 'Manage team access' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="group bg-white rounded-2xl p-5 border shadow-brand hover:shadow-accent transition-all duration-200 hover:-translate-y-0.5"
                style={{ borderColor: '#BFC9D1' }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors" style={{ backgroundColor: '#EAEFEF' }}>
                  <Icon className="w-5 h-5 group-hover:scale-110 transition-transform" style={{ color: '#FF9B51' }} />
                </div>
                <h3 className="text-sm font-bold mb-0.5" style={{ color: '#25343F' }}>{item.label}</h3>
                <p className="text-xs" style={{ color: '#BFC9D1' }}>{item.sub}</p>
              </Link>
            );
          })}
        </div>

      </div>
    </AdminLayout>
  );
};

export default Dashboard;
